import { Suspense, useEffect } from 'react';

import MainApp from 'components/MainApp';
import 'normalize.css';
import Websocket from 'services/Websocket';
import { useProductType } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setGlobalParams, setLocalization } from 'store/slices/global';
import { useGetLocalizationQuery } from 'store/slices/global/services/globalApi';
import { formatInitialQueryParams, getDirection, getUserLanguage, localStorageSet } from 'utils';

enum WsFlowTypes {
  deposit = 'deposit',
  payout = 'payout',
  topUp = 'top-up',
}

const App = () => {
  const dispatch = useAppDispatch();
  const initialQueryParams = formatInitialQueryParams(window.location.search);
  const { token: authToken, languageIso2, pageMode } = initialQueryParams;
  const websocketUrl = window.location.origin;
  // const websocketUrl = 'https://payment.cryptopay-dev.space-app.io'; // replace for sockets working locally
  const locationPathname = window.location.pathname.substring(1);
  const wsPathUrl = [WsFlowTypes.deposit, WsFlowTypes.topUp].includes(
    window.location.pathname.substring(1) as WsFlowTypes,
  )
    ? '/hubs/paymentIntent'
    : '/hubs/payoutIntent';
  const userLanguages = languageIso2 || getUserLanguage();
  const shouldOpenWS = [WsFlowTypes.deposit, WsFlowTypes.payout, WsFlowTypes.topUp].includes(
    locationPathname as WsFlowTypes,
  );

  const { isLoading: isLocalizationLoading, data: dataLocalization = {} } = useGetLocalizationQuery(
    {
      languageIso2: userLanguages,
      authToken,
    },
  );
  useEffect(() => {
    let websocketPaymentIntentInstance: Websocket | undefined;
    if (shouldOpenWS) {
      websocketPaymentIntentInstance = new Websocket(websocketUrl, wsPathUrl, authToken);
    }
    localStorageSet('authToken', initialQueryParams.token);
    const queryData = {
      ...initialQueryParams,
      currentLanguage: userLanguages,
    };
    dispatch(setGlobalParams(queryData));
    document.documentElement.dir = getDirection(userLanguages);
    document.documentElement.lang = userLanguages;

    return () => {
      if (websocketPaymentIntentInstance) {
        websocketPaymentIntentInstance.closeWebSocket();
      }
    };
  }, []);

  const { productType, isCryptoProcessing } = useProductType();
  const productTypeResolved = useAppSelector((state) => state.global.productTypeResolved);

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement && pageMode) {
      rootElement.classList.add('isPageMode');
    }
  }, []);

  useEffect(() => {
    if (!productTypeResolved) return;

    const isNotFoundPage = window.location.pathname === '/not-found';

    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.classList.remove('product-cryptopay', 'product-cryptoprocessing');
      rootElement.classList.add(`product-${productType.toLowerCase()}`);
    }

    if (isNotFoundPage) return;

    const faviconPath = isCryptoProcessing ? '/xbo/favicon.ico' : '/favicon.ico';
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconPath;

    document.title = isCryptoProcessing ? 'CryptoProcessing' : 'Cryptopay';
  }, [productType, productTypeResolved]);

  useEffect(() => {
    if (dataLocalization?.resources && Object.keys(dataLocalization.resources).length) {
      dispatch(
        setLocalization({
          resources: dataLocalization.resources,
          languageIso2: userLanguages,
        }),
      );
    }
  }, [isLocalizationLoading]);

  return (
    <Suspense fallback='...'>
      <MainApp
        pageMode={pageMode}
        productType={productType}
        isLocalizationLoading={isLocalizationLoading}
      />
    </Suspense>
  );
};

export default App;
